import PropTypes from "prop-types";
import { Tabs } from "antd";
import ReservationDetailsHeader from "./header/ReservationDetailsHeader";
import StartReservationBanner from "./banners/StartReservationBanner";
import EndReservationBanner from "./banners/EndReservationBanner";
import VehicleCard from "./vehicle/VehicleCard";
import PaymentSummaryCard from "./overview/PaymentSummaryCard";
import InsuranceDocumentsCard from "./insurance/InsuranceDocumentsCard";
import CustomerProfileCard from "./overview/CustomerProfileCard";
import LifecycleTimeline from "./overview/LifecycleTimeline";
import PhotosAndRecordsPanel from "./photos-and-records/PhotosAndRecordsPanel";
import ConfirmReject from "./actions/ConfirmReject";
import { ReservationDetailsPageSkeleton } from "./loading-skeleton";
import ReservationActivityLogSection from "./activity-log/ReservationActivityLogSection";
import {
    canRejectUpcomingReservation,
    getReservationBusinessId,
    shouldShowEndBanner,
    shouldShowStartBanner,
} from "./utils";

/** Reservation details UI for active, upcoming, ongoing, and past (`past-reservation`) records. */
const ReservationDetailsPage = ({
    t,
    match,
    isLoading,
    resDetails,
    setResDetails,
    customerDetails,
    carDetails,
    insurancePolicyData,
    isInsuranceLoading,
    fetchResDetails,
    onCarSwapSuccess,
    onOpenInvoice,
    canRefreshInvoice,
    onRefreshInvoice,
    isRefreshingInvoice,
    openRefundModal,
    isRefundModalOpen,
    closeRefundModal,
    openPaymentOptionModal,
    closePaymentOptionModal,
    isPaymentOptionOpen,
    overDueModalOpen,
    overDueModalClose,
    isOpenExtraChargeModal,
    apiClient,
    isRejecting,
    setIsRejecting,
    rejectReservation,
}) => {
    if (isLoading) {
        return <ReservationDetailsPageSkeleton />;
    }

    const showStartBanner = shouldShowStartBanner(resDetails);
    const showEndBanner = shouldShowEndBanner(resDetails);
    const needsLegacyActions = Boolean(
        resDetails &&
            (!resDetails.isApproved ||
                !resDetails.isCarLinked ||
                !resDetails.isCarSelected ||
                (resDetails.isDoorUnlockedRequested && !resDetails.isDoorUnlockedApproved)),
    );
    const showHeaderReject =
        canRejectUpcomingReservation(resDetails) && !needsLegacyActions && typeof rejectReservation === "function";

    const reservationBusinessId = getReservationBusinessId(resDetails, match?.params?.id);
    const activityLogRefreshKey = resDetails?.updatedAt ?? reservationBusinessId;

    const overviewContent = (
        <div className="flex min-w-0 flex-col gap-3">
            <PaymentSummaryCard
                resDetails={resDetails}
                match={match}
                customerDetails={customerDetails}
                fetchResDetails={fetchResDetails}
                openRefundModal={openRefundModal}
                isRefundModalOpen={isRefundModalOpen}
                closeRefundModal={closeRefundModal}
                openPaymentOptionModal={openPaymentOptionModal}
                closePaymentOptionModal={closePaymentOptionModal}
                isPaymentOptionOpen={isPaymentOptionOpen}
                overDueModalOpen={overDueModalOpen}
                overDueModalClose={overDueModalClose}
                isOpenExtraChargeModal={isOpenExtraChargeModal}
                t={t}
            />
            <InsuranceDocumentsCard
                resDetails={resDetails}
                insurancePolicyData={insurancePolicyData}
                isInsuranceLoading={isInsuranceLoading}
                onRefresh={fetchResDetails}
                t={t}
            />
        </div>
    );

    const detailsSidebar = (
        <aside
            className="flex min-w-0 flex-col gap-3 xl:sticky xl:top-3 xl:max-h-[calc(100vh-1.5rem)] xl:self-start xl:overflow-y-auto">
            <CustomerProfileCard resDetails={resDetails} customerDetails={customerDetails} t={t} />
            <LifecycleTimeline resDetails={resDetails} t={t} />
        </aside>
    );

    const tabItems = [
        {
            key: "overview",
            label: t("upcomingDetails.overview"),
            children: overviewContent,
        },
        {
            key: "photos",
            label: t("upcomingDetails.photosAndRecords"),
            children: <PhotosAndRecordsPanel resDetails={resDetails} t={t} />,
        },
    ];

    return (
        <div className="flex min-w-0 flex-col gap-3">
            <ReservationDetailsHeader
                resDetails={resDetails}
                customerDetails={customerDetails}
                t={t}
                onOpenInvoice={onOpenInvoice}
                canRefreshInvoice={canRefreshInvoice}
                onRefreshInvoice={onRefreshInvoice}
                isRefreshingInvoice={isRefreshingInvoice}
                showReject={showHeaderReject}
                onRejectReservation={rejectReservation}
                isRejecting={isRejecting}
            />

            {showStartBanner ? <StartReservationBanner resDetails={resDetails} t={t} /> : null}

            {showEndBanner ? <EndReservationBanner resDetails={resDetails} t={t} /> : null}

            <VehicleCard
                resDetails={resDetails}
                carDetails={carDetails}
                setResDetails={setResDetails}
                onCarSwapSuccess={onCarSwapSuccess}
                onReservationExtended={fetchResDetails}
                t={t}
            />

            <div className="flex min-w-0 flex-col gap-3 xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(280px,320px)] xl:items-start">
                <div className="order-1 min-w-0 xl:col-start-1 xl:row-start-1">
                    <Tabs
                        defaultActiveKey="overview"
                        items={tabItems}
                        className="reservation-details-tabs min-w-0 [&_.ant-tabs-nav]:before:border-b-[#f0f0f0] [&_.ant-tabs-nav-list]:flex-nowrap [&_.ant-tabs-nav-wrap]:overflow-x-auto [&_.ant-tabs-tab]:whitespace-nowrap"
                        tabBarStyle={{ marginBottom: 12 }}
                    />
                </div>
                <div className="order-2 min-w-0 xl:col-start-2 xl:row-start-1 xl:row-span-2">{detailsSidebar}</div>
                {reservationBusinessId ? (
                    <div className="order-3 min-w-0 xl:col-start-1 xl:row-start-2">
                        <ReservationActivityLogSection
                            reservationId={reservationBusinessId}
                            refreshKey={activityLogRefreshKey}
                            t={t}
                        />
                    </div>
                ) : null}
            </div>

            {needsLegacyActions ? (
                <ConfirmReject
                    resDetails={resDetails}
                    setResDetails={setResDetails}
                    match={match}
                    apiClient={apiClient}
                    isRejecting={isRejecting}
                    setIsRejecting={setIsRejecting}
                    isLoading={false}
                    rejectReservation={rejectReservation}
                    onCarSwapSuccess={onCarSwapSuccess}
                    t={t}
                />
            ) : null}
        </div>
    );
};

ReservationDetailsPage.propTypes = {
    t: PropTypes.func.isRequired,
    match: PropTypes.object,
    isLoading: PropTypes.bool,
    resDetails: PropTypes.object,
    setResDetails: PropTypes.func,
    customerDetails: PropTypes.object,
    carDetails: PropTypes.object,
    insurancePolicyData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    isInsuranceLoading: PropTypes.bool,
    fetchResDetails: PropTypes.func,
    onCarSwapSuccess: PropTypes.func,
    onOpenInvoice: PropTypes.func.isRequired,
    canRefreshInvoice: PropTypes.bool,
    onRefreshInvoice: PropTypes.func,
    isRefreshingInvoice: PropTypes.bool,
    openRefundModal: PropTypes.func,
    isRefundModalOpen: PropTypes.bool,
    closeRefundModal: PropTypes.func,
    openPaymentOptionModal: PropTypes.func,
    closePaymentOptionModal: PropTypes.func,
    isPaymentOptionOpen: PropTypes.bool,
    overDueModalOpen: PropTypes.func,
    overDueModalClose: PropTypes.func,
    isOpenExtraChargeModal: PropTypes.bool,
    apiClient: PropTypes.object,
    isRejecting: PropTypes.bool,
    setIsRejecting: PropTypes.func,
    rejectReservation: PropTypes.func,
};

export default ReservationDetailsPage;
